import CheckIcon from '@mui/icons-material/CheckRounded';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Box, ListItemIcon } from '@mui/material';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  selectIsAdmin,
  selectOrganizationName,
  selectOrganizations,
  setActiveOrganizationId,
  selectActiveOrganizationId,
  selectIsDisabled,
} from '@/entities/session';
import { ROUTES } from '@/shared/constants';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import {
  MenuItemWithOptions,
  OrganizationInfoIcon,
  UserManagementIcon,
} from '@/shared/ui';
import { GroupItem } from '../GroupItem/GroupItem';
import { MenuItem } from '../GroupItem/MenuItem';

interface Props {
  expanded: boolean;
}

export function OrganizationItem({ expanded }: Props) {
  const dispatch = useAppDispatch();
  const isUserDisabled = useAppSelector(selectIsDisabled);
  const organizationName = useAppSelector(selectOrganizationName);
  const organizations = useAppSelector(selectOrganizations);
  const sortedOrganizations = useMemo(() => {
    return organizations?.toSorted((a, b) => {
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
  }, [organizations]);
  const activeOrganizationId = useAppSelector(selectActiveOrganizationId);
  const isAdmin = useAppSelector(selectIsAdmin);

  const handleChangeOrganization = (organizationId: string) => () => {
    dispatch(setActiveOrganizationId(organizationId));
  };

  return (
    <GroupItem
      expanded={expanded}
      icon={
        <Box
          sx={{
            width: '100%',
            height: '100%',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '2rem',
            fontWeight: 500,
          }}
        >
          {organizationName?.slice(0, 1).toUpperCase()}
        </Box>
      }
      title={organizationName!}
      menuContent={[
        ...(!isUserDisabled
          ? [
              <MenuItem
                key="organization-info"
                component={Link}
                to={ROUTES.organizationInfo}
              >
                <ListItemIcon>
                  <OrganizationInfoIcon />
                </ListItemIcon>
                Organization Info
              </MenuItem>,
            ]
          : []),
        ...(isAdmin
          ? [
              <MenuItem
                key="user-management"
                component={Link}
                to={ROUTES.userManagement}
              >
                <ListItemIcon>
                  <UserManagementIcon />
                </ListItemIcon>
                User Management
              </MenuItem>,
            ]
          : []),
        ...(sortedOrganizations.length > 1
          ? [
              <MenuItemWithOptions
                key="change-organization"
                label="Change Organization"
                icon={<SwapHorizIcon />}
              >
                {sortedOrganizations.map((organization) => (
                  <MenuItem
                    key={organization.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                    onClick={handleChangeOrganization(organization.id)}
                  >
                    {organization.name}
                    {activeOrganizationId === organization.id && (
                      <CheckIcon fontSize="small" color="success" />
                    )}
                  </MenuItem>
                ))}
              </MenuItemWithOptions>,
            ]
          : []),
      ]}
    />
  );
}
